import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { forgotPassword, verifyOtp, resetPassword, resendOtp } from '../../services/authApi';

const ForgotPasswordModal = ({ open, onClose, onSwitchToLogin }) => {
  // Steps: 'email' -> 'otp' -> 'reset'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Timer for OTP expiry
  useEffect(() => {
    let interval;
    if (step === 'otp' && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, otpTimer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    setStep('email');
    setEmail('');
    setOtp(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setResetToken('');
    setError('');
    setLoading(false);
    setOtpTimer(0);
    setCanResend(false);
    onClose();
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Only allow single digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const response = await forgotPassword(email);
      if (response.success) {
        setStep('otp');
        setOtpTimer(120); // 2 minutes
        setCanResend(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setLoading(true);

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const response = await verifyOtp(email, otpCode);
      if (response.success) {
        setResetToken(response.resetToken);
        setStep('reset');
      }
    } catch (err) {
      setError(err.message);
      // Reset OTP if verification failed
      if (err.message?.includes('expired') || err.message?.includes('attempts')) {
        setOtp(['', '', '', '', '', '']);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await resetPassword(resetToken, newPassword);
      if (response.success) {
        // Success - close modal and switch to login
        handleClose();
        if (onSwitchToLogin) onSwitchToLogin();
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await resendOtp(email);
      if (response.success) {
        setOtpTimer(120); // Reset timer
        setCanResend(false);
        setOtp(['', '', '', '', '', '']); // Clear OTP inputs
      }
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtp(['', '', '', '', '', '']);
    setOtpTimer(0);
    setCanResend(false);
    setError('');
  };

  const renderEmailStep = () => (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 1,
          pt: 2,
          px: 3,
          flexDirection: 'column',
          gap: 0.5,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#20202A',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Forgot Password
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: '#666',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Enter your registered email to reset your password
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 1.5,
          pt: 1.5,
          flex: 1,
          overflowY: 'hidden'
        }}
      >
        <Box component="form" onSubmit={handleSendOtp} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif' }}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#20202A',
                mb: 0.5
              }}
            >
              Email Address
            </Typography>
            <TextField
              type="email"
              placeholder="Enter your registered email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  backgroundColor: 'rgba(232, 233, 237, 1)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  '& fieldset': {
                    borderColor: 'transparent'
                  },
                  '&:hover fieldset': {
                    borderColor: '#20202A'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#20202A',
                    borderWidth: '0.5px'
                  },
                  '& .MuiInputBase-input': {
                    padding: '6px 0',
                    fontSize: { xs: '16px', sm: '16px', md: '14px' },
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#999',
                  opacity: 1
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1.5,
          flexDirection: 'column',
          gap: 1,
          borderTop: 'none'
        }}
      >
        <Button
          onClick={handleSendOtp}
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            backgroundColor: '#20202A',
            color: '#FBFBFB',
            borderRadius: '18px',
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk',
            minHeight: '44px',
            '&:hover': {
              backgroundColor: '#333'
            },
            '&:disabled': {
              backgroundColor: '#ccc'
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Code'}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mt: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              color: '#666'
            }}
          >
            Remember your password?
          </Typography>
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleClose();
              if (onSwitchToLogin) onSwitchToLogin();
            }}
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              color: '#1976d2',
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Login
          </Link>
        </Box>
      </DialogActions>
    </>
  );

  const renderOtpStep = () => (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 1,
          pt: 2,
          px: 3,
          flexDirection: 'column',
          gap: 0.5,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={handleBackToEmail}
          sx={{
            position: 'absolute',
            left: 16,
            top: 16,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#20202A',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Verify Code
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: '#666',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Enter the 6-digit code sent to {email}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 1.5,
          pt: 1.5,
          flex: 1,
          overflowY: 'hidden'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif', mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {/* OTP Input Boxes */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                id={`otp-${index}`}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: 'center', fontSize: '24px', fontWeight: '700' }
                }}
                sx={{
                  width: '44px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(232, 233, 237, 1)',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: 'transparent'
                    },
                    '&:hover fieldset': {
                      borderColor: '#20202A'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#20202A',
                      borderWidth: '0.5px'
                    },
                    '& .MuiInputBase-input': {
                      padding: '12px 0',
                      textAlign: 'center'
                    }
                  }
                }}
              />
            ))}
          </Box>

          {/* Timer */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: otpTimer > 0 ? '#20202A' : '#999',
                fontWeight: 500
              }}
            >
              Code expires in {formatTime(otpTimer)}
            </Typography>
          </Box>

          {/* Resend Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: '#666'
              }}
            >
              Didn't receive the code?{' '}
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (canResend || otpTimer === 0) {
                    handleResendOtp();
                  }
                }}
                sx={{
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  color: (canResend || otpTimer === 0) ? '#1976d2' : '#999',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: (canResend || otpTimer === 0) ? 'underline' : 'none'
                  },
                  pointerEvents: (canResend || otpTimer === 0) ? 'auto' : 'none'
                }}
              >
                Resend Code
              </Link>
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1.5,
          flexDirection: 'column',
          gap: 1,
          borderTop: 'none'
        }}
      >
        <Button
          onClick={handleVerifyOtp}
          variant="contained"
          fullWidth
          disabled={loading || otp.join('').length !== 6}
          sx={{
            backgroundColor: '#20202A',
            color: '#FBFBFB',
            borderRadius: '18px',
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk',
            minHeight: '44px',
            '&:hover': {
              backgroundColor: '#333'
            },
            '&:disabled': {
              backgroundColor: '#ccc'
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify Code'}
        </Button>
      </DialogActions>
    </>
  );

  const renderResetStep = () => (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 1,
          pt: 2,
          px: 3,
          flexDirection: 'column',
          gap: 0.5,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#20202A',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Reset Password
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: '#666',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Enter your new password
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 1.5,
          pt: 1.5,
          flex: 1,
          overflowY: 'hidden'
        }}
      >
        <Box component="form" onSubmit={handleResetPassword} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif' }}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#20202A',
                mb: 0.5
              }}
            >
              New Password
            </Typography>
            <TextField
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              disabled={loading}
              helperText="Minimum 6 characters"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  backgroundColor: 'rgba(232, 233, 237, 1)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  '& fieldset': {
                    borderColor: 'transparent'
                  },
                  '&:hover fieldset': {
                    borderColor: '#20202A'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#20202A',
                    borderWidth: '0.5px'
                  },
                  '& .MuiInputBase-input': {
                    padding: '6px 0',
                    fontSize: { xs: '16px', sm: '16px', md: '14px' },
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#999',
                  opacity: 1
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '12px'
                }
              }}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#20202A',
                mb: 0.5
              }}
            >
              Confirm Password
            </Typography>
            <TextField
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              required
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  backgroundColor: 'rgba(232, 233, 237, 1)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  '& fieldset': {
                    borderColor: 'transparent'
                  },
                  '&:hover fieldset': {
                    borderColor: '#20202A'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#20202A',
                    borderWidth: '0.5px'
                  },
                  '& .MuiInputBase-input': {
                    padding: '6px 0',
                    fontSize: { xs: '16px', sm: '16px', md: '14px' },
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#999',
                  opacity: 1
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1.5,
          flexDirection: 'column',
          gap: 1,
          borderTop: 'none'
        }}
      >
        <Button
          onClick={handleResetPassword}
          variant="contained"
          fullWidth
          disabled={loading || !newPassword || !confirmPassword}
          sx={{
            backgroundColor: '#20202A',
            color: '#FBFBFB',
            borderRadius: '18px',
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk',
            minHeight: '44px',
            '&:hover': {
              backgroundColor: '#333'
            },
            '&:disabled': {
              backgroundColor: '#ccc'
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Reset Password'}
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        timeout: 0
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          width: {
            xs: '90vw',
            sm: '592px',
            md: '592px',
            lg: '592px'
          },
          maxWidth: '592px',
          display: 'flex',
          flexDirection: 'column',
          transition: 'none !important',
          m: 2
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            transition: 'opacity 0ms !important'
          }
        }
      }}
    >
      {step === 'email' && renderEmailStep()}
      {step === 'otp' && renderOtpStep()}
      {step === 'reset' && renderResetStep()}
    </Dialog>
  );
};

export default ForgotPasswordModal;
